import { EventPushResponse } from "sbtech-sports-data-api-client";
import { SportDataGateWayRetriever, initialSportsDataFeed } from "./SportDataGateWayRetriever";
import { observe, Lambda, observable, action } from "mobx";
import { Time, OddStyles, UserInfo } from "sb-resp-lib";
import { SuperBannerConfiguration } from "../";
import { DeviceDetectHelper } from "../../parser";
import { InternalMessageBus, InternalMessageBusMessage } from "sb-resp-lib/lib/InternalMessageBus";
import { InternalMessageBusChannels } from "sb-resp-lib";
import { OddStyles as OddStyleMap }  from "../../data/models/Selection";

export type DataUtilsType = {
    betSlipUtil: BetSlipUtil;
    deviceDetect: DeviceDetectHelper;
    navHandler: Application;
    timeControl: TimeControl;
};

export type DataFacadeDataComposite = {
    eventsIds: number[];
    sportDataGatewayRetriever: SportDataGateWayRetriever;
    cms: SuperBannerConfiguration;
    utils: DataUtilsType;
    userInfo: UserInfo;
    currentOddStyleIndex: number;
    blockUniqueId: string;
    currentSelectedSportId: number;
    messageBus: InternalMessageBus;
    dateFormat: string;
    timeFormat: string;
};

/*
    The class has 'istanbul ignore' flag , because of testing typescript interface with Jest.
    It`s known issue : (https://github.com/kulshekhar/ts-jest/issues/378)
    The class is fully tested, but propery "sportsDataChangeFeed" implement interface "ChangeFeedUpdate" which is not
    testable with Jest - there is no types like in typescript
 */

/* istanbul ignore next */
export class DataFacade {

    cms: SuperBannerConfiguration;
    @observable dataCollection: EventPushResponse | undefined;
    @observable eventsIds: number[];
    sportDataGateway: SportDataGateWayRetriever;
    disposeSportsDataFeed!: Lambda;

    disposeRemoveOfEvents!: Lambda;

    @observable shouldRemoveEvents: boolean;

    disposeTimeZone!: Lambda;
    betSlipUtil: BetSlipUtil;
    navHandler: Application;
    @observable currentSelectedSportId: number;
    @observable currentTimeZone: number;
    dateFormat: string;
    timeFormat: string;
    @observable currentOddStyle: string;
    @observable deviceDetectHelper: DeviceDetectHelper;
    @observable isUserLoggedIn: boolean;
    eventsManager: EventsManager;
    timeControl: TimeControl;
    timeInstance: Time;
    userInfo: UserInfo;

    blockUniqueId: string;
    messageBus: InternalMessageBus;

    constructor(options: DataFacadeDataComposite) {

        this.dataCollection = initialSportsDataFeed;
        this.eventsIds = options.eventsIds;
        this.cms = options.cms;
        this.sportDataGateway = options.sportDataGatewayRetriever;
        this.betSlipUtil = options.utils.betSlipUtil;
        this.navHandler = options.utils.navHandler;
        this.deviceDetectHelper = options.utils.deviceDetect;
        this.isUserLoggedIn = false;
        this.shouldRemoveEvents = false;

        this.blockUniqueId = options.blockUniqueId;
        this.eventsManager = new EventsManager();
        this.timeControl = options.utils.timeControl;
        this.currentTimeZone = this.timeControl.getCurrentTimeZone();
        this.dateFormat = options.dateFormat;
        this.timeFormat = options.timeFormat;
        this.currentOddStyle = OddStyles[options.currentOddStyleIndex];
        this.timeInstance = Time.getInstance;
        this.userInfo = options.userInfo;
        this.messageBus = options.messageBus;

        let timeProvider = {
            getServerDate: this.timeControl.getServerTime,
            currentTimeZone: this.timeControl.getCurrentTimeZone().toString()
        };
        this.timeInstance.init(timeProvider);

        this.setIsUserLoggedIn(!!this.userInfo.current);
        this.userInfo.onLogin[options.blockUniqueId] = this.setIsUserLoggedIn.bind(this, true);
        this.userInfo.onLogout[options.blockUniqueId] = this.setIsUserLoggedIn.bind(this, false);

        this.updateCollection = this.updateCollection.bind(this);

        this.cms.DisplayMobileMode = this.deviceDetectHelper.deviceDetect.isMobile &&
            this.cms.DisplayMobileMode;
        this.cms.DisplayMultipleItems = (this.deviceDetectHelper.deviceDetect.isDesktop &&
            this.cms.DisplayMultipleItems) ||
            this.deviceDetectHelper.deviceDetect.isTablet ||
            this.deviceDetectHelper.deviceDetect.isMobile;
        this.currentSelectedSportId = options.currentSelectedSportId;
        this.updateOddStyleOfEventsFromMessage = this.updateOddStyleOfEventsFromMessage.bind(this);
        this.updateOddStyleOfEventsOnRequest = this.updateOddStyleOfEventsOnRequest.bind(this);
        // observe this.sportDataGateway["sportsDataChangeFeed"]
        // for changes and call "updateCollection" with changeFeed from Sports Data API

    }

    // should activate main flow of the Facade, activate from outside
    activate(): void {
        this.disposeSportsDataFeed = observe(this.sportDataGateway, "sportsDataChangeFeed", (change) => {
            this.updateCollection(change.newValue);
        });

        this.disposeRemoveOfEvents = observe(this.sportDataGateway, "shouldRemoveEvents", (change) => {
            this.shouldRemoveEvents = change.newValue;
        });

        this.disposeTimeZone = observe(this.timeInstance, (change) => {
            let changedObject = change.object as Time;
            change.name === "currentTimeZone" && this.updateTimeZone(changedObject.currentTimeZone);
        });

        this.messageBus.on(InternalMessageBusChannels.legacy.oddStyle.update, this.updateOddStyleOfEventsFromMessage);

        this.subscribeForHighlightEvents();
        this.eventsManager.addEventListener("oddStyleChanged", this, this.updateOddStyleOfEvents, true);
        this.eventsManager.addEventListener("onBranchRemoved", this, this.resetCurrentSelectedSportId, true);
        this.requestOddStyle();
        this.updateTimeZone(this.timeControl.getCurrentTimeZone());
    }

    deactivate(): void {
        this.disposeSportsDataFeed();
        this.sportDataGateway.deactivate();
        this.eventsManager.removeEventListener("oddStyleChanged", this, true);
        this.eventsManager.removeEventListener("onBranchRemoved", this, true);
        this.messageBus.off(InternalMessageBusChannels.legacy.oddStyle.update, this.updateOddStyleOfEventsFromMessage);
    }

    subscribeForHighlightEvents(): void {
        this.sportDataGateway.activate(this.eventsIds);
    }

    updateCollection(dataFeed: EventPushResponse | undefined): void {
        this.dataCollection = dataFeed;
    }

    @action.bound updateTimeZone(newTime: number): void {
        if (!isNaN(newTime)) {
            this.currentTimeZone = newTime;
        }
    }

    @action.bound updateOddStyleOfEvents(oddStyle: number): void {
        if (!isNaN(oddStyle)) {
            this.currentOddStyle = OddStyleMap[OddStyles[oddStyle]];
        }
    }

    @action setIsUserLoggedIn(isUserLoggedIn: boolean): void {
        this.isUserLoggedIn = isUserLoggedIn;
    }

    @action.bound resetCurrentSelectedSportId(): void {
        this.setCurrentSelectedSportId(0);
    }

    @action.bound setCurrentSelectedSportId(sportId: number): void {
        if (sportId >= 0 )
        {
            this.currentSelectedSportId = sportId;
        }
    }

    requestOddStyle() {
        this.messageBus.emit(InternalMessageBusChannels.legacy.oddStyle.request,
            { callback: this.updateOddStyleOfEventsOnRequest });
    }

    updateOddStyleOfEventsFromMessage(message: InternalMessageBusMessage<{ currentSelectedOddStyle: number }>) {
        if (message && message.data && message.data[0] && typeof (message.data[0].currentSelectedOddStyle) !== "undefined") {
            this.updateOddStyleOfEvents(message.data[0].currentSelectedOddStyle);
        }
    }

    updateOddStyleOfEventsOnRequest(message) {
        if(message) {
            this.updateOddStyleOfEvents(message.currentSelectedOddStyle);
        }
    }
}
